import React, {FC, FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {KTIcon} from "../../../../_metronic/helpers";
import {PLACEHOLDER_IMAGE_URL} from "../../../mock/constants";
import {useIntl} from "react-intl";

interface AvatarUploadProps extends React.HTMLAttributes<HTMLFormElement> {
  onImageUpload?: (file: File | null, preview: string | null) => void;
  onImageRemove?: () => void;
  defaultImage?: string;
  className?: string;
  maxSize?: number;
  imageSizes?: "banner" | "avatar";
  type?: "avatar" | "brand";
  buttonClasses?: {
    change?: string;
    remove?: string;
    submit?: string;
  };
  labels?: {
    title?: string;
    label?: string;
    save?: string;
  };
}

const getDefaultValue = <T, >(value: T | undefined, defaultValue: T): T => {
  return value !== undefined ? value : defaultValue;
};


export const UploadImage: FC<AvatarUploadProps> = ({
                                                     onImageUpload,
                                                     maxSize = 10,
                                                     defaultImage = PLACEHOLDER_IMAGE_URL,
                                                     className = '',
                                                     buttonClasses = {},
                                                     imageSizes,
                                                     onImageRemove,
                                                     type,
                                                     labels = {}
                                                   }) => {

  const mergedButtonClasses = {
    change: getDefaultValue(buttonClasses.change, 'btn btn-sm btn-icon bg-white'),
    remove: getDefaultValue(buttonClasses.remove, 'btn btn-sm btn-icon btn-light-danger'),
    submit: getDefaultValue(buttonClasses.submit, 'btn btn-sm btn-primary'),
  };

  const [showRemoveIcon, setShowRemoveIcon] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(defaultImage || PLACEHOLDER_IMAGE_URL);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {formatMessage} = useIntl();


  useEffect(() => {
    if (defaultImage) {
      setImagePreview(defaultImage);
    }
  }, [defaultImage]);

  const handleImageChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      if (file.size > maxSize * 1024 * 1024) {
        alert(`Fayl hajmi ${maxSize} MB dan oshmasligi kerak`);
        return;
      }

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          setImage(file);
          setImagePreview(result);
          setShowRemoveIcon(true);

          if (onImageUpload) {
            onImageUpload(file, result);
          }
        };
        reader.readAsDataURL(file);
      } else {
        alert("Allowed formats: png, jpg, jpeg");
      }
    }
  }, [onImageUpload, maxSize]);

  const handleRemoveImage = useCallback(async () => {
    setImage(null);
    setImagePreview(defaultImage);
    setShowRemoveIcon(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (onImageUpload) {
      onImageUpload(null, null);
    }

    if (onImageRemove) {
      onImageRemove();
    }
  }, [onImageUpload, defaultImage, onImageRemove]);

  const [width, height] = imageSizes === "banner" ? [300, 200] : imageSizes === "avatar" ? [100, 100] : [150, 150];

  return (
      <div className={`${className}`}>
        <div style={{
          width: width
        }}>
          <div>
            <div className="position-relative">
              <img
                  id="avatar-preview"
                  src={imagePreview}
                  alt="avatar"
                  className="rounded-4 img-fluid"
                  style={{width: width, height: height, objectFit: 'cover'}}
              />
              <button
                  type="button"
                  id="change-avatar"
                  className={`${mergedButtonClasses.change} position-absolute shadow`}
                  style={{bottom: -10, right: 5, zIndex: 1, width: '30px', height: '30px'}}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Change avatar"
              >
                <KTIcon iconName="pencil" iconType="solid"/>
              </button>

              {showRemoveIcon && (
                  <button
                      type="button"
                      id="remove-avatar"
                      className={`${mergedButtonClasses.remove} position-absolute shadow`}
                      style={{bottom: -10, right: 40, zIndex: 1, width: '30px', height: '30px'}}
                      onClick={handleRemoveImage}
                      aria-label="Remove avatar"
                  >
                    <KTIcon iconName="cross" iconType="solid"/>
                  </button>
              )}

              <input
                  type="file"
                  className="form-control d-none"
                  id="file-input"
                  ref={fileInputRef}
                  accept=".jpeg,.jpg,.png"
                  onChange={handleImageChange}
                  aria-hidden="true"
              />
            </div>

          </div>
        </div>
        <p className="small text-gray-500 fw-bold mt-5">
          {formatMessage({id: "Ruxsat berilgan formatlar: png, jpg, jpeg"})}
        </p>
      </div>
  );
};
